import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Paper,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  IconButton,
  Fade,
  Skeleton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  Stack,
  Divider,
  alpha,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPatients,
  getPatientDetails,
} from "../../features/patients/patientSlice";
import {
  fetchConsultations,
  createConsultation,
  completeConsultation,
} from "../../features/consultations/consultationSlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { styled } from '@mui/material/styles';

import PatientCard from "../../components/PatientCard";
import ConsultationTimer from "../../components/ConsultationTimer";
import ConsultationTabs from "../../components/ConsultationTabs";
import PrescriptionModal from "../../components/PrescriptionModal";
import ConsultationPreviewModal from "../../components/ConsultationPreviewModal";
import PatientDetails from "./PatientDetails";

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(8px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const Consultation = () => {
  const { appointmentId, patientId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Redux state
  // const { data: patients, loading, selectedPatient, error: patientError } = useSelector(
  //   (state) => state.patients
  // );

  const {
  data: patients,
  loading,
  error,
  selectedPatient,
  selectedLoading,
  selectedError,
  patientAppointments,
} = useSelector((state) => ({
  data:                 state.patients.data,
  loading:              state.patients.loading,
  error:                state.patients.error,
  selectedPatient:      state.patients.selectedPatient,
  selectedLoading:      state.patients.selectedLoading,
  selectedError:        state.patients.selectedError,
  patientAppointments:  state.patients.patientAppointments,
}));

  const { data: consultations, loading: consultationLoading, error: consultationError } = useSelector(
    (state) => state.consultations
  );

  // Local state
  const [selectedPatientId, setSelectedPatientId] = useState(patientId || "");
  const [consultationId, setConsultationId] = useState(null);
  const [consultationStarted, setConsultationStarted] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [prescriptionModalOpen, setPrescriptionModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [patientDetailsOpen, setPatientDetailsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [consultationData, setConsultationData] = useState({
    symptoms: "",
    diagnosis: "",
    prescriptions: [],
    testsOrdered: [],
    referrals: [],
    notes: "",
    follow_up_required: false,
    follow_up_date: null,
  });

  // Fetch patients & consultations on mount with error handling
  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchPatients({ page: 1, page_size: 50 })).unwrap();
      } catch (error) {
        setErrorMessage('Unable to load patients. Please try again later.');
        toast.error('Failed to load patients', {
          position: "top-center",
          autoClose: 5000,
        });
      }
    };
    fetchData();
  }, [dispatch]);

  // Fetch patient details when patientId changes
  useEffect(() => {
    if (patientId) {
      const fetchPatientDetails = async () => {
        try {
          await dispatch(getPatientDetails(patientId)).unwrap();
        } catch (error) {
          setErrorMessage('Unable to load patient details. Please try again later.');
          toast.error('Failed to load patient details', {
            position: "top-center",
            autoClose: 5000,
          });
        }
      };
      fetchPatientDetails();
    }
  }, [dispatch, patientId]);

  // Auto-save consultation data
  useEffect(() => {
    if (consultationStarted && hasUnsavedChanges) {
      const saveTimeout = setTimeout(() => {
        handleSaveConsultation();
      }, 3000); // Auto-save after 3 seconds of inactivity
      return () => clearTimeout(saveTimeout);
    }
  }, [consultationData, consultationStarted, hasUnsavedChanges]);

  // Handle patient selection
  const handlePatientChange = (event) => {
    const selectedId = event.target.value;
    setSelectedPatientId(selectedId);
    dispatch(getPatientDetails(selectedId));
  };

  

  // Handle consultation data changes
  const handleConsultationDataChange = (newData) => {
    setConsultationData(prev => ({ ...prev, ...newData }));
    setHasUnsavedChanges(true);
  };

  // Handle consultation start
  const handleStartConsultation = async () => {
    if (!selectedPatientId) {
      toast.error("Please select a patient before starting the consultation.", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    try {
      const response = await dispatch(
        createConsultation({ patient: selectedPatientId, appointment: appointmentId })
      ).unwrap();

      if (response?.id) {
        setConsultationId(response.id);
        setConsultationStarted(true);
        toast.success("Consultation session started successfully!", {
          position: "top-center",
          icon: "🏥",
        });
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (error) {
      console.error("Error in handleStartConsultation:", error);
      toast.error("Unable to start consultation. Please try again.", {
        position: "top-center",
      });
    }
  };

  // Handle consultation completion
  const handleCompleteConsultation = async () => {
    if (!consultationId) {
      toast.error("No active consultation to complete.", {
        position: "top-center",
      });
      return;
    }

    // Validate required fields
    if (!consultationData.symptoms || !consultationData.diagnosis) {
      toast.error("Please fill in all required fields before completing the consultation.", {
        position: "top-center",
      });
      return;
    }

    setPreviewModalOpen(true);
  };

  // Handle consultation save
  const handleSaveConsultation = async () => {
    if (!consultationId || !consultationStarted) return;

    setIsSaving(true);
    try {
      // Here you would typically dispatch an action to save the consultation data
      // For now, we'll just simulate a save
      await new Promise(resolve => setTimeout(resolve, 1000));
      setHasUnsavedChanges(false);
      toast.success("Consultation data saved successfully!", {
        position: "top-center",
        autoClose: 3000,
      });
    } catch (error) {
      toast.error("Failed to save consultation data", {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle step change
  const handleStepChange = (step) => {
    setActiveStep(step);
    setTabValue(step);
  };

  // Handle consultation completion confirmation
  const handleConfirmCompletion = async () => {
    if (!consultationId) {
      toast.error("No consultation found to complete.", {
        position: "top-center",
      });
      return;
    }
  
    const updatedConsultationData = {
      symptoms: consultationData.symptoms || "",
      diagnosis: Array.isArray(consultationData.diagnoses)
        ? consultationData.diagnoses.join(", ")
        : consultationData.diagnosis || "",
      notes: consultationData.notes || "",
      follow_up_required: consultationData.follow_up_required || false,
      follow_up_date: consultationData.follow_up_date || null,
      is_completed: true,
  
      prescription:
        consultationData.prescription?.medications?.length > 0
          ? {
              medications: consultationData.prescription.medications.map((med) => ({
                medication_name: med.medication || med.medication_name,
                dosage: med.dosage || "",
                frequency: med.frequency || "",
                instructions: med.instructions || "",
              })),
            }
          : null,
  
      tests_ordered: consultationData.testsOrdered?.length
        ? consultationData.testsOrdered.map((test) => ({
            test_name: test.test_name || test.testName,
            instructions: test.instructions || "",
            status: test.status || "Pending",
          }))
        : [],
  
      referrals: consultationData.referrals?.length
        ? consultationData.referrals.map((referral) => ({
            referred_to: referral.referred_to || referral.specialist,
            reason: referral.reason || "",
          }))
        : [],
    };
  
    try {
      await dispatch(completeConsultation({ 
        consultationId, 
        consultationData: {
          ...updatedConsultationData,
          is_completed: true,
          appointment_id: appointmentId,
        },
      })).unwrap();
      
      toast.success("Consultation completed successfully!", {
        position: "top-center",
        icon: "✅",
      });
      navigate("/doctor-dashboard/consultation-history");
    } catch (error) {
      console.error("Error completing consultation:", error);
      toast.error("Unable to complete consultation. Please try again.", {
        position: "top-center",
      });
    }
  };

  return (
    <Box sx={{ 
      padding: { xs: 2, sm: 3 },
      minHeight: '100vh',
      bgcolor: 'background.default'
    }}>
      {/* Error Snackbar */}
      <Snackbar 
        open={!!errorMessage} 
        autoHideDuration={6000} 
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setErrorMessage('')} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <IconButton 
          onClick={() => navigate(-1)}
          sx={{ 
            alignSelf: { xs: 'flex-start', sm: 'center' }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography 
          variant="h4" 
          sx={{ 
            fontSize: { xs: '1.5rem', sm: '2rem' },
            flex: 1
          }}
        >
          {selectedPatient
            ? `Consultation for ${selectedPatient?.name}`
            : "New Consultation"}
        </Typography>
        {consultationStarted && (
          <Box sx={{ 
            display: 'flex',
            gap: 2,
            width: { xs: '100%', sm: 'auto' }
          }}>
            <ConsultationTimer
              consultationStarted={consultationStarted}
              timeElapsed={timeElapsed}
              setTimeElapsed={setTimeElapsed}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleCompleteConsultation}
              startIcon={<CheckIcon />}
              sx={{
                borderRadius: '8px',
                py: 1,
                px: { xs: 2, sm: 3 },
                flex: { xs: 1, sm: 'initial' },
              }}
            >
              Complete
            </Button>
          </Box>
        )}
      </Box>

      {!consultationStarted ? (
        <Fade in={!consultationStarted}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 2, sm: 3 },
              borderRadius: '16px',
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <FormControl 
              fullWidth 
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            >
              <InputLabel id="patient-select-label">Select Patient</InputLabel>
              <Select
                labelId="patient-select-label"
                id="patient-select"
                value={selectedPatientId}
                onChange={handlePatientChange}
                disabled={loading}
              >
                {loading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 2 }} />
                    Loading patients...
                  </MenuItem>
                ) : (
                  patients.map((patient) => (
                    <MenuItem 
                      key={patient.id} 
                      value={patient.id}
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderRadius: '4px',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 2
                      }}>
                        <PersonIcon color="action" />
                        <Typography>{patient.name}</Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {loading ? (
              <Box sx={{ mb: 3 }}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px' }} />
              </Box>
            ) : selectedPatient ? (
              <Box sx={{ mb: 3 }}>
                <PatientCard patient={selectedPatient} />
              </Box>
            ) : null}

            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleStartConsultation}
              disabled={!selectedPatientId || loading}
              fullWidth
              sx={{
                py: 1.5,
                borderRadius: '8px',
                fontSize: '1rem',
                textTransform: 'none',
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ mr: 1 }} />
              ) : (
                <>
                  <AccessTimeIcon sx={{ mr: 1 }} />
                  Start Consultation
                </>
              )}
            </Button>
          </Paper>
        </Fade>
      ) : (
        <Fade in={consultationStarted}>
          <Box>
            {loading ? (
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px', mb: 3 }} />
            ) : selectedPatient ? (
              <Box sx={{ mb: 3 }}>
                <PatientCard patient={selectedPatient} />
              </Box>
            ) : null}

            {consultationStarted && (
              <ConsultationTabs
                tabValue={tabValue}
                handleChange={(event, newValue) => setTabValue(newValue)}
                consultationData={consultationData}
                setConsultationData={handleConsultationDataChange}
              />
            )}

            {/* <Stepper activeStep={activeStep} orientation="vertical">
              {['Symptoms', 'Diagnosis', 'Prescriptions', 'Tests', 'Referrals', 'Follow-up & Notes'].map((label, index) => (
                <Step key={label}>
                  <StepLabel>
                    <Typography variant="subtitle1">{label}</Typography>
                  </StepLabel>
                  <StepContent>
                    <Box sx={{ mb: 2 }}>
                      <ConsultationTabs
                        tabValue={index}
                        handleChange={(event, newValue) => handleStepChange(newValue)}
                        consultationData={consultationData}
                        setConsultationData={handleConsultationDataChange}
                        isMobile={isMobile}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                      <Button
                        disabled={index === 0}
                        onClick={() => handleStepChange(index - 1)}
                      >
                        Back
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => handleStepChange(index + 1)}
                        disabled={index === 5}
                      >
                        {index === 5 ? 'Finish' : 'Next'}
                      </Button>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper> */}
            

            {/* Auto-save indicator */}
            {hasUnsavedChanges && (
              <Box sx={{ 
                position: 'fixed', 
                bottom: 16, 
                right: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'background.paper',
                p: 1,
                borderRadius: 1,
                boxShadow: 3,
                zIndex: 1000
              }}>
                {isSaving ? (
                  <>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Saving...</Typography>
                  </>
                ) : (
                  <>
                    <InfoIcon color="info" />
                    <Typography variant="body2">Unsaved changes</Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Fade>
      )}

      {/* Modals */}
      <PrescriptionModal
        open={prescriptionModalOpen}
        onClose={() => setPrescriptionModalOpen(false)}
        consultationData={consultationData}
        setConsultationData={handleConsultationDataChange}
      />

      <ConsultationPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        onConfirm={handleConfirmCompletion}
        consultationData={consultationData}
      />

      <Dialog
        open={patientDetailsOpen}
        onClose={() => setPatientDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Patient Details</Typography>
            <IconButton onClick={() => setPatientDetailsOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPatient && <PatientDetails patientId={selectedPatient.id} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Consultation;
